import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import StaticMap from 'components/StaticMap';
import MapSettings from 'components/Map/settings';
import { zoomToFeature, calculateArea } from 'components/StaticMap/helper';
import { removeAlertByGroup } from 'redux/alerts/actions';
import { useComponentMount } from 'utilities/hooks.util';
import Terraformer from 'terraformer-arcgis-parser';

export const MapView: React.FC<Props> = ({
  baseMapTheme,
  enquiryShape,
  onMapLoaded,
  setLualExtent,
  onZoomChange
}) => {
  const mapObjRef = useRef<any>({});
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [shapeSet, setShapeSet] = useState<boolean>(false);
  const shapeZoomRef = useRef<{ extent: any; bufferSize: number } | null>(null);
  const dispatch = useDispatch();

  const zoomToEnquiryShape = useCallback(() => {
    if (!shapeZoomRef.current) {
      return;
    }

    const { extent, bufferSize } = shapeZoomRef.current;
    mapObjRef.current.shapeZoomExtent = shapeZoomRef.current;
    zoomToFeature(mapObjRef.current, extent, bufferSize);
  }, []);

  const onMapReady = () => {
    const { loadedView } = mapObjRef.current;
    if (loadedView) {
      loadedView
        .when(() => {
          loadedView.on('layerview-create', () => {
            if (onMapLoaded) {
              onMapLoaded();
            }
          });

          setTimeout(() => {
            setMapReady(true);
            if (shapeZoomRef.current) {
              zoomToEnquiryShape();
            }
          }, 100);
        })
        .catch((error: any) => {
          console.error('Error initializing map view:', error);
        });
    }
  };

  const setEnquiryShapeToMap = useCallback(
    (shape: any) => {
      const { loadedMapModules, loadedMap } = mapObjRef.current;
      if (loadedMapModules && loadedMapModules.WebMercatorUtils) {
        const { Graphic, WebMercatorUtils, GraphicsLayer } = loadedMapModules;
        let currentFeatureLayer = loadedMap.findLayerById(
          'current-enquiry-layer'
        );

        if (!currentFeatureLayer) {
          currentFeatureLayer = new GraphicsLayer({
            id: 'current-enquiry-layer'
          });
          loadedMap.add(currentFeatureLayer, 0);
        }

        const geometry: any = Terraformer.convert(shape);
        const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
          geometry
        );
        const graphic = new Graphic({
          geometry: {
            ...webMercatorGeometry,
            type: 'polygon'
          },
          symbol:
            baseMapTheme === 'dark'
              ? MapSettings.polygonDarkSymbol
              : MapSettings.polygonSymbol
        });

        if (currentFeatureLayer.graphics.length === 0) {
          currentFeatureLayer.add(graphic);
        }

        const activeGraphic = currentFeatureLayer.graphics.getItemAt(0);
        const graphicExtent = activeGraphic.geometry.extent;
        const area = calculateArea(mapObjRef.current, activeGraphic.geometry);
        const bufferSize = area > 0 ? Math.sqrt(area) * 0.2 : 0.2;

        shapeZoomRef.current = { extent: graphicExtent, bufferSize };
        mapObjRef.current.mapOriginalExtent = graphicExtent;
        zoomToEnquiryShape();

        const bufferSizeInMeters = bufferSize * 1000;
        setLualExtent &&
          setLualExtent({
            xmin: graphicExtent.xmin - bufferSizeInMeters,
            ymin: graphicExtent.ymin - bufferSizeInMeters,
            xmax: graphicExtent.xmax + bufferSizeInMeters,
            ymax: graphicExtent.ymax + bufferSizeInMeters,
            spatialReference: graphicExtent.spatialReference
          });

        setShapeSet(true);
      }
    },
    [baseMapTheme, setLualExtent, zoomToEnquiryShape]
  );

  useEffect(() => {
    mapReady && enquiryShape && setEnquiryShapeToMap(enquiryShape);
  }, [enquiryShape, mapReady, setEnquiryShapeToMap]);

  // Handle zoom change separately after shape is set
  useEffect(() => {
    if (mapReady && shapeSet && onZoomChange) {
      // Small delay to ensure zoom animation completes
      const timer = setTimeout(() => {
        const { loadedView } = mapObjRef.current;
        if (loadedView && typeof onZoomChange === 'function') {
          onZoomChange(loadedView.zoom);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [mapReady, shapeSet, onZoomChange]);

  useComponentMount(() => {
    return () => {
      dispatch(removeAlertByGroup('map-alert'));
      if (mapObjRef.current.loadedMap) {
        mapObjRef.current.loadedMap.removeAll();
        mapObjRef.current.loadedMap.destroy();
        mapObjRef.current.loadedMap = null;
      }
      if (mapObjRef.current.loadedView) {
        mapObjRef.current.loadedView.destroy();
        mapObjRef.current.loadedView = null;
      }
    };
  });

  return (
    <div className="map--view">
      <StaticMap
        onReady={onMapReady}
        mapObject={mapObjRef}
        onZoomChange={onZoomChange}
      />
    </div>
  );
};

type ownProps = {
  enquiryShape?: any;
  baseMapTheme?: String;
  onMapLoaded?: Function;
  setLualExtent?: Function;
  onZoomChange?: Function;
};

type Props = ownProps;

export default React.memo(MapView);
